'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { FormRenderer } from '@jlg-website/form/renderer/client';
import type { Answers, FormConfig, FormInput, FormLocale } from '@jlg-website/shared';
import type { FormProps, FormVariant } from './type';
import { useLeadForm } from '@/hooks/use-lead-form';
import { buildRenderCompletion } from './render-completion';

type FormClientProps = {
  schema: FormInput;
  variant?: FormVariant;
  config?: FormConfig;
  locale?: FormLocale;
  /** Answers to pre-select, decoded from the URL server-side (see `answer-query`). */
  initialAnswers?: Answers;
  completionAction?: FormProps['completionAction'];
  /** Page the visitor came from; when set, the first step's Prev navigates back here. */
  backRoute?: string;
};

export default function FormClient({
  schema,
  variant,
  config,
  locale,
  initialAnswers,
  completionAction,
  backRoute,
}: FormClientProps) {
  const router = useRouter();
  // Validators, marketing events and the submit path are shared with the mass-tort hero client.
  const {
    config: configWithValidators,
    handleSubmit,
    handleStepNext,
    handleReachLastStep,
  } = useLeadForm(schema, config);

  // Only when the visitor arrived from the same-slug page (its answers are in the URL) does the
  // first step get a Prev that takes them back there; a direct visit leaves it disabled.
  const handleFirstStepPrev = useCallback(() => {
    if (backRoute) router.push(backRoute);
  }, [router, backRoute]);

  return (
    <FormRenderer
      schema={schema}
      config={configWithValidators}
      variant={variant}
      locale={locale}
      autoNext
      // Pre-selected from the URL token (server-decoded); with `autoNext`, a fully answered leading
      // step advances on its own.
      initialAnswers={initialAnswers}
      onSubmit={handleSubmit}
      onStepNext={handleStepNext}
      // A `redirect` fires `onRedirect` (not `onStepNext`), so wire the same progress event here
      // to keep emitting `emitStepProgress` when the visitor is redirected.
      onRedirect={handleStepNext}
      onReachLastStep={handleReachLastStep}
      enableNextOnValidate={false}
      renderCompletion={buildRenderCompletion(configWithValidators.completionMessage)}
      completionActionLabel={completionAction?.label}
      onCompletionAction={
        completionAction ? () => router.push(completionAction.href) : undefined
      }
      enablePrevOnFirstStep={Boolean(backRoute)}
      onFirstStepPrev={handleFirstStepPrev}
    />
  );
}
